import { msg } from "@lingui/core/macro";
import { createEffect, createMemo, createSignal, For, Show } from "solid-js";
import { useSearch } from "@tanstack/solid-router";
import { Card, CardContent, CardHeader, CardTitle } from "@fleet/ui/card";
import { Button, buttonVariants } from "@/components/ui/button";
import { Badge } from "@fleet/ui/badge";
import { Skeleton } from "@fleet/ui/skeleton";
import { QueryErrorState } from "@/components/ui/query-error-state";
import { ConfirmDialog } from "@/components/ui/confirm-dialog";
import { useI18n } from "@/providers/i18n-provider";
import { api, createApiQuery, SERVER_BASE_URL } from "@/lib/api";
import { queryClient } from "@/lib/query-client";
import { PROVIDERS_STALE_TIME_MS } from "@/lib/constants";
import { handleApiResponse, queryKeys, sharedApi } from "@agape/shared";
import { showToast } from "@/components/ui/toast";
import { Link2, ShieldCheck } from "@fleet/icons";

type ProviderId = "google" | "apple";

const PROVIDER_LABELS: Record<ProviderId, string> = {
  google: "Google",
  apple: "Apple",
};

/**
 * "Connected accounts": allows linking/unlinking Google and Apple from the
 * profile. Only shows providers ENABLED by the backend. Respects the
 * safeguard for the last method (the backend rejects leaving the user without access).
 */
export function ConnectedAccountsSection() {
  const { t } = useI18n();
  // Reads ?linked=<provider> to show feedback after returning from the callback.
  const search = useSearch({ strict: false }) as () => { linked?: string };
  const [confirmUnlink, setConfirmUnlink] = createSignal<ProviderId | null>(null);
  const [busy, setBusy] = createSignal(false);

  // P23/CC-60 · P26: the two `queryFn`s manually wrote `data ?? {…}` over
  // openapi-fetch, so a 503 resolved in SUCCESS with a FABRICATED object:
  // `isError` never arrived, the error branch was dead code, and the
  // screen claimed «Not connected» / «This is your only sign-in method» for
  // an account whose real state was unknown. `createApiQuery` goes through
  // `handleApiResponse`, which THROWS on any non-2xx — the same semantics as
  // the rest of the repo, and now the same in `social-auth-buttons.tsx`,
  // which shares the `queryKey` `auth.socialProviders`.
  const providersQuery = createApiQuery(
    () => queryKeys.auth.socialProviders(),
    async () => sharedApi.socialProviders(api),
    () => ({ staleTime: PROVIDERS_STALE_TIME_MS, retry: false }),
  );

  const methodsQuery = createApiQuery(
    () => queryKeys.auth.loginMethods(),
    async () => sharedApi.loginMethods(api),
    () => ({ retry: false }),
  );

  /**
   * P23/CC-60 · P26: the section must be shown ALSO when loading failed.
   * Without this, on error `enabledProviders()` is `[]` and the OUTER
   * `<Show>` hides the ENTIRE card: neither content nor notice, and the
   * error box becomes unreachable by another route. The measured symptom
   * was not “missing the error message”; it was “the section does not appear”.
   */
  const hasLoadFailure = () => providersQuery.isError || methodsQuery.isError;

  const enabledProviders = createMemo<ProviderId[]>(() => {
    // P23/I2: `.data` ONLY in `isSuccess` — it is the only field of the
    // solid-query proxy that suspends, and it suspends while there is no value.
    if (!providersQuery.isSuccess) {
      return [];
    }
    const p = providersQuery.data;
    if (!p) {
      return [];
    }
    const list: ProviderId[] = [];
    if (p.google) {
      list.push("google");
    }
    if (p.apple) {
      list.push("apple");
    }
    return list;
  });

  // P23/I2: with `login-methods` down, reading `.data` without `isSuccess` made
  // the screen say «Not connected» for a Google account that WAS connected.
  const linkedProviders = createMemo(() =>
    methodsQuery.isSuccess ? (methodsQuery.data?.providers ?? []) : [],
  );
  const hasPassword = createMemo(() =>
    methodsQuery.isSuccess ? (methodsQuery.data?.has_password ?? false) : false,
  );

  const isLinked = (provider: ProviderId) => linkedProviders().includes(provider);

  // Total number of sign-in methods (password + each linked social account).
  const totalMethods = createMemo(() => linkedProviders().length);

  // Is this the LAST method? (it cannot be unlinked without setting up another).
  const isLastMethod = (provider: ProviderId) =>
    isLinked(provider) && totalMethods() <= 1 && !hasPassword();

  const linkUrl = (provider: ProviderId) =>
    `${SERVER_BASE_URL.replace(/\/$/, "")}/api/auth/link-social/${provider}`;

  const handleUnlink = async (provider: ProviderId) => {
    setBusy(true);
    try {
      // CC-30: openapi-fetch does NOT throw on 4xx/5xx. `handleApiResponse` forces
      // the throw (with the body message) so a 409/500 never passes as silent
      // success; the catch also covers network failures (which do throw).
      await handleApiResponse(sharedApi.unlinkAccount(api, provider));
      showToast(t(msg`Account unlinked.`), "success");
      await queryClient.invalidateQueries({ queryKey: queryKeys.auth.loginMethods() });
    } catch (err) {
      const message = err instanceof Error ? err.message : "";
      if (message.includes("only sign-in method")) {
        showToast(t(msg`Set a password before unlinking your only sign-in method.`), "error");
      } else {
        showToast(t(msg`Could not unlink the account. Please try again.`), "error");
      }
    } finally {
      setBusy(false);
      setConfirmUnlink(null);
    }
  };

  // Feedback on returning from the linking flow (?linked=<provider>).
  let linkedFeedbackDone = false;
  createEffect(() => {
    const provider = search().linked;
    if (provider && !linkedFeedbackDone) {
      linkedFeedbackDone = true;
      showToast(t(msg`Account connected.`), "success");
      void queryClient.invalidateQueries({ queryKey: queryKeys.auth.loginMethods() });
    }
  });

  return (
    <Show when={enabledProviders().length > 0 || hasLoadFailure()}>
      <Card>
        <CardHeader class="flex flex-row items-center gap-2 space-y-0">
          <ShieldCheck class="h-5 w-5 text-primary" />
          <CardTitle>{t(msg`Connected accounts`)}</CardTitle>
        </CardHeader>
        <CardContent class="space-y-3">
          {/* P23/I3 + P26: sin estado de error, `login-methods` (o `providers`)
              caído pintaba «Not connected» de una cuenta que SÍ estaba
              vinculada. Se ENVUELVE el bloque entero: encadenar
              `!isPending && !isError` dejaría error y esqueleto a la vez (en
              error `isPending` es `false`). */}
          <Show when={hasLoadFailure()}>
            <QueryErrorState
              testId="connected-accounts-error"
              compact
              onRetry={() => {
                // Retries ONLY the one(s) that failed.
                if (providersQuery.isError) {
                  void providersQuery.refetch();
                }
                if (methodsQuery.isError) {
                  void methodsQuery.refetch();
                }
              }}
            />
          </Show>
          <Show when={!hasLoadFailure()}>
            <Show
              when={!methodsQuery.isPending && !providersQuery.isPending}
              fallback={
                <div class="space-y-3">
                  <Skeleton class="h-14 w-full rounded-xl" />
                  <Skeleton class="h-14 w-full rounded-xl" />
                </div>
              }
            >
              <For each={enabledProviders()}>
                {(provider) => (
                  <div class="flex items-center gap-4 rounded-xl border border-border bg-muted/30 p-4">
                    <div class="flex h-10 w-10 items-center justify-center rounded-full bg-muted text-muted-foreground">
                      <Link2 class="h-5 w-5" />
                    </div>
                    <div class="min-w-0 flex-1">
                      <p class="text-sm font-semibold">{PROVIDER_LABELS[provider]}</p>
                      <Show
                        when={isLinked(provider)}
                        fallback={
                          <p class="text-xs text-muted-foreground">{t(msg`Not connected`)}</p>
                        }
                      >
                        <Badge
                          variant="secondary"
                          class="mt-1 h-5 border-none bg-primary/20 px-1.5 text-[10px] font-bold uppercase tracking-wider text-primary"
                        >
                          {t(msg`Connected`)}
                        </Badge>
                      </Show>
                    </div>

                    <Show
                      when={isLinked(provider)}
                      fallback={
                        <a
                          href={linkUrl(provider)}
                          class={buttonVariants({ variant: "outline", size: "sm" })}
                          data-testid={`link-${provider}`}
                        >
                          {t(msg`Connect`)}
                        </a>
                      }
                    >
                      <Button
                        variant="ghost"
                        size="sm"
                        disabled={busy() || isLastMethod(provider)}
                        title={
                          isLastMethod(provider)
                            ? t(msg`Set a password before unlinking your only sign-in method.`)
                            : undefined
                        }
                        onClick={() => setConfirmUnlink(provider)}
                        class="text-muted-foreground hover:bg-destructive/10 hover:text-destructive"
                      >
                        {t(msg`Disconnect`)}
                      </Button>
                    </Show>
                  </div>
                )}
              </For>

              <Show when={!hasPassword() && totalMethods() <= 1}>
                <p class="text-xs text-muted-foreground">
                  {t(
                    msg`This is your only sign-in method. Set a password to be able to disconnect it.`,
                  )}
                </p>
              </Show>
            </Show>
          </Show>
        </CardContent>
      </Card>

      <ConfirmDialog
        open={confirmUnlink() !== null}
        onOpenChange={(open) => {
          if (!open) {
            setConfirmUnlink(null);
          }
        }}
        title={t(msg`Disconnect account?`)}
        description={t(
          msg`You will no longer be able to sign in with this provider. You can reconnect it anytime.`,
        )}
        confirmLabel={t(msg`Disconnect`)}
        cancelLabel={t(msg`Cancel`)}
        onConfirm={() => {
          const provider = confirmUnlink();
          if (provider) {
            void handleUnlink(provider);
          }
        }}
      />
    </Show>
  );
}
